import * as React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '../../components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Home,
  ListOrdered,
  BarChart2,
  Users,
  Settings,
  LogOut,
  Building2,
  User2,
  ChevronUp,
  AlertCircle,
  Leaf,
  UserCheck,
  BookOpen,
  ChevronLeft,
  TowerControl,
  File,
  Bike,
} from 'lucide-react';
import { useSelector, useDispatch } from 'react-redux';
import { version } from '../../version.js';
import { doResetImpersonation } from '../../modules/auth/stores/thunk';
import { ROLES } from '../../constants';
import { AppDispatch, RootState } from '../../redux/store';
import { useFeatureFlags } from '../../modules/feature-flags/feature-flags.hook';
import { useMemo } from 'react';
import { selectActiveUnseenCount } from '../../modules/issues/stores/slice';
import { useHistory } from 'react-router-dom';
import { Button } from '../ui/button';
import { SelectCompanyModal } from '../Pages/Home/SelectCompanyModal';
import { selectActiveClaimCount } from '../../modules/claims/stores/slice';
import { useEffect } from 'react';
import { doGetUnseen } from '../../modules/issues/stores/thunk';
import { doGetActiveClaimsCount } from '../../modules/claims/stores/thunk';

export function AppSidebar() {
  const user = useSelector((state: RootState) => state.auth.user);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { featureFlags } = useFeatureFlags();
  const activeUnseenCount = useSelector((state: RootState) => selectActiveUnseenCount(state));
  const activeClaimCount = useSelector((state: RootState) => selectActiveClaimCount(state));
  const history = useHistory();
  const sidebar = useSidebar();

  useEffect(() => {
    const fetchCounts = async () => {
      await dispatch(doGetUnseen({}));
      await dispatch(doGetActiveClaimsCount());
    };
    fetchCounts();
  }, [location.pathname, user]);

  // --- Route groups ---
  const paths = useMemo(
    () => ({
      home: `/home`,
      companies: `/companies`,
      users: `/users`,
      performance: `/performance`,
      orders: `/orders`,
      settings: `/settings`,
      issues: `/issues`,
      serviceLevel: '/service-level',
      sustainability: '/sustainability',
      customerJourney: '/customer-journey',
      controlTower: '/control-tower',
      claims: '/claims',
      carriers: '/carriers',
    }),
    [featureFlags],
  );

  const merchantRoutes = [
    { title: 'Home', url: paths.home, icon: <Home className="mr-2 w-4 h-4" /> },
    { title: 'Orders', url: paths.orders, icon: <ListOrdered className="mr-2 w-4 h-4" /> },
    { title: 'Performance', url: paths.performance, icon: <BarChart2 className="mr-2 w-4 h-4" /> },
    { title: 'Sustainability', url: paths.sustainability, icon: <Leaf className="mr-2 w-4 h-4" /> },
    { title: 'Customer Journey', url: paths.customerJourney, icon: <BookOpen className="mr-2 w-4 h-4" /> },
    {
      title: 'Issues',
      url: paths.issues,
      icon: <AlertCircle className="mr-2 w-4 h-4" />,
      badge: activeUnseenCount > 0 ? (activeUnseenCount > 99 ? '99+' : activeUnseenCount.toString()) : undefined,
    },
    {
      title: 'Claims',
      url: paths.claims,
      icon: <File className="mr-2 w-4 h-4" />,
    },
  ];

  const carrierRoutes = [
    { title: 'Home', url: paths.home, icon: <Home className="mr-2 w-4 h-4" /> },
    { title: 'Orders', url: paths.orders, icon: <ListOrdered className="mr-2 w-4 h-4" /> },
    { title: 'Service Level', url: paths.serviceLevel, icon: <UserCheck className="mr-2 w-4 h-4" /> },
    {
      title: 'Issues',
      url: paths.issues,
      icon: <AlertCircle className="mr-2 w-4 h-4" />,
      badge: activeUnseenCount > 0 ? (activeUnseenCount > 99 ? '99+' : activeUnseenCount.toString()) : undefined,
    },
    {
      title: 'Claims',
      url: paths.claims,
      icon: <File className="mr-2 w-4 h-4" />,
      badge: activeClaimCount > 0 ? (activeClaimCount > 99 ? '99+' : activeClaimCount.toString()) : undefined,
    },
  ];

  const adminRoutes = [
    { title: 'Control Tower', url: paths.controlTower, icon: <TowerControl className="mr-2 w-4 h-4" /> },
    { title: 'Merchants', url: paths.companies, icon: <Building2 className="mr-2 w-4 h-4" /> },
    { title: 'Carriers', url: paths.carriers, icon: <Bike className="mr-2 w-4 h-4" /> },
    { title: 'Users', url: paths.users, icon: <Users className="mr-2 w-4 h-4" /> },
    // Add more admin-only routes here if needed
  ];

  // --- Role-based grouping ---
  let merchantItems = [];
  let carrierItems = [];
  let adminItems = [];

  if (user.role === ROLES.CARRIER_USER) {
    carrierItems = carrierRoutes;
  } else if (user.role === ROLES.COMPANY_OWNER || user.role === ROLES.LOCATION_USER) {
    // DKJ users should not see the customer journey menu item
    const isDKJUser = user.role === ROLES.COMPANY_OWNER && (user.companyId === 34 || user.companyId === 35);
    merchantItems = isDKJUser ? merchantRoutes.filter((item) => item.url !== paths.customerJourney) : merchantRoutes;
  } else if (user.role === ROLES.ADMIN) {
    merchantItems = merchantRoutes;
    // carrierItems = carrierRoutes;
    adminItems = adminRoutes; // TODO: SHOW ALL CARRIER AND MERCHANT ROUTES TO ADMIN: FIX SERVICE LEVEL ROUTE ACCESS TO ADMINS
  }

  // --- Sidebar footer settings options ---
  const handleSignOut = () => {
    window.location.href = '/logout';
  };

  const handleStopImpersonation = async () => {
    await dispatch(doResetImpersonation());
    window.location.reload();
  };
  const showCompanySelect = useMemo(() => user.role === ROLES.COMPANY_OWNER && user.companies.length > 1, [user]);
  return (
    <Sidebar collapsible="icon" style={{ ['--sidebar-width-icon' as any]: '4rem' }}>
      <SidebarHeader>
        {sidebar.state === 'collapsed' ? (
          <div className="flex items-center justify-center w-full py-4">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={sidebar.toggleSidebar}>
              <img
                src="/sidebar/sparqle-logo.svg"
                alt="logo"
                style={{
                  width: 30,
                  minWidth: 30,
                  maxWidth: 30,
                  cursor: 'pointer',
                }}
              />
            </Button>
          </div>
        ) : (
          <div className="p-4 flex items-center justify-between">
            <img src="/sidebar/sparqle-with-name.svg" alt="logo" style={{ width: 120, minWidth: 30, maxWidth: 120 }} />
            <SidebarTrigger aria-label="Collapse sidebar">
              <ChevronLeft />
            </SidebarTrigger>
          </div>
        )}
        {showCompanySelect && <SelectCompanyModal user={user} collapsed={sidebar.state === 'collapsed'} />}
      </SidebarHeader>

      <SidebarContent className="ml-2 mr-2">
        {merchantItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>
              {user.role === ROLES.COMPANY_OWNER
                ? user?.company?.name
                : user.role === ROLES.LOCATION_USER
                  ? user?.location?.name
                  : 'Merchant'}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {merchantItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname.startsWith(item.url)}
                      onClick={() => history.push(item.url)}
                    >
                      {item.icon}
                      {item.title}
                      {item.badge && (
                        <SidebarMenuBadge className="bg-red-500 text-white text-xs px-1 rounded">
                          {item.badge}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {carrierItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{user.role === ROLES.CARRIER_USER ? user?.carrier?.name : ''}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {carrierItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname.startsWith(item.url)}
                      onClick={() => history.push(item.url)}
                    >
                      {item.icon}
                      {item.title}
                      {item.badge && (
                        <SidebarMenuBadge className="bg-red-500 text-white text-xs px-1 rounded">
                          {item.badge}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {adminItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      isActive={location.pathname.startsWith(item.url)}
                      onClick={() => history.push(item.url)}
                    >
                      {item.icon}
                      {item.title}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  style={user.impersonatedBy ? { border: '2px solid orange', boxSizing: 'border-box' } : undefined}
                  className="py-3"
                >
                  <User2 className="mr-2 w-4 h-4" />
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs text-gray-500">{user?.email}</span>
                  </div>
                  <ChevronUp className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-[--radix-popper-anchor-width]">
                <DropdownMenuItem onClick={() => history.push('/settings')} className="hover:cursor-pointer">
                  <Settings className="mr-2 w-4 h-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut} className="hover:cursor-pointer">
                  <LogOut className="mr-2 w-4 h-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
                {user.impersonatedBy && (
                  <DropdownMenuItem onClick={handleStopImpersonation}>
                    <span>Stop impersonating</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem>
                  <span>Version {version}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

export default AppSidebar;
